import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlannerViewData } from '~/routes/calendar._index';

export interface HoveredJobPill {
  jobId: string;
  site: PlannerViewData[number];
  date: Date;
  element: HTMLElement;
}

interface UseHoverDialogOptions {
  /** Parent scroll container ref - to hide on scroll */
  parentRef: React.RefObject<HTMLDivElement>;
  /** Whether on mobile device (disables hover on mobile) */
  isMobile: boolean;
}

interface UseHoverDialogResult {
  /** Currently hovered job pill data */
  hoveredJobPill: HoveredJobPill | null;
  /** Set the hovered job pill */
  setHoveredJobPill: React.Dispatch<
    React.SetStateAction<HoveredJobPill | null>
  >;
  /** Handler for job pill mouse enter */
  handleJobPillMouseEnter: (
    jobId: string,
    site: PlannerViewData[number],
    date: Date,
    element: HTMLElement
  ) => void;
  /** Handler for job pill mouse leave */
  handleJobPillMouseLeave: () => void;
  /** Handler for popup mouse enter (keeps dialog open) */
  handlePopupMouseEnter: () => void;
  /** Handler for popup mouse leave */
  handlePopupMouseLeave: () => void;
  /** Floating UI props for the reference element */
  getReferenceProps: (
    userProps?: React.HTMLProps<Element>
  ) => Record<string, unknown>;
  /** Floating UI props for the floating element */
  getFloatingProps: (
    userProps?: React.HTMLProps<HTMLElement>
  ) => Record<string, unknown>;
  /** Floating UI styles for positioning */
  floatingStyles: React.CSSProperties;
  /** Ref for the floating element */
  refs: {
    setFloating: (node: HTMLElement | null) => void;
    setReference: (node: Element | null) => void;
  };
  /** Whether floating element is open */
  isOpen: boolean;
}

/**
 * Hook for managing the hover dialog for job pills.
 * Uses Floating UI for positioning instead of Popper.js.
 *
 * Features:
 * - Delayed show/hide with configurable timing
 * - Hide on scroll
 * - Keep open when hovering the popup itself
 * - Mobile-aware (disabled on mobile)
 */
export function useHoverDialog({
  parentRef,
  isMobile,
}: UseHoverDialogOptions): UseHoverDialogResult {
  const [hoveredJobPill, setHoveredJobPill] = useState<HoveredJobPill | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);

  // Timeout refs for delayed show/hide
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Ref to track hovered pill for scroll handler
  const hoveredJobPillRef = useRef(hoveredJobPill);
  useEffect(() => {
    hoveredJobPillRef.current = hoveredJobPill;
  }, [hoveredJobPill]);

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top-start',
    middleware: [
      offset(({ placement }) => {
        // Different offsets based on placement
        if (placement.startsWith('bottom')) {
          return { mainAxis: 20, crossAxis: 60 };
        }
        return { mainAxis: 6, crossAxis: -8 };
      }),
      flip({
        fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
        padding: 8,
      }),
      shift({
        padding: 14,
        crossAxis: true,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Hover interaction with delay group support
  const hover = useHover(context, {
    delay: { open: 200, close: 150 },
    restMs: 40,
    enabled: !isMobile,
  });

  // Dismiss on escape or click outside
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  // Sync isOpen with hoveredJobPill
  useEffect(() => {
    setIsOpen(hoveredJobPill !== null);
  }, [hoveredJobPill]);

  // Handle job pill mouse enter
  const handleJobPillMouseEnter = useCallback(
    (
      jobId: string,
      site: PlannerViewData[number],
      date: Date,
      element: HTMLElement
    ) => {
      if (isMobile) return;

      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = undefined;
      }

      // Clear any pending show timeout
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = undefined;
      }

      // Check if we're hovering a job in the same cell
      const isSameCell =
        hoveredJobPill &&
        hoveredJobPill.site.id === site.id &&
        hoveredJobPill.date.getTime() === date.getTime();

      if (isSameCell) {
        // Same cell - don't update the dialog, keep it open
        return;
      }

      // Different cell or no current hover - use delay
      showTimeoutRef.current = setTimeout(() => {
        // Set the reference element for Floating UI
        refs.setReference(element);
        setHoveredJobPill({ jobId, site, date, element });
      }, 200);
    },
    [hoveredJobPill, isMobile, refs]
  );

  // Handle job pill mouse leave
  const handleJobPillMouseLeave = useCallback(() => {
    if (isMobile) return;

    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }

    // Set hide timeout
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredJobPill(null);
    }, 150);
  }, [isMobile]);

  // Handle popup mouse enter (keeps dialog open)
  const handlePopupMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  }, []);

  // Handle popup mouse leave
  const handlePopupMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredJobPill(null);
    }, 150);
  }, []);

  // Hide dialog on scroll
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (hoveredJobPillRef.current) {
        // Clear any pending show timeouts
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
          showTimeoutRef.current = undefined;
        }
        // Clear any pending hide timeouts
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = undefined;
        }
        // Hide dialog immediately
        setHoveredJobPill(null);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [parentRef]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return {
    hoveredJobPill,
    setHoveredJobPill,
    handleJobPillMouseEnter,
    handleJobPillMouseLeave,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    refs: {
      setFloating: refs.setFloating,
      setReference: refs.setReference,
    },
    isOpen,
  };
}

// Re-export Floating UI components for use in PlannerView
export { FloatingDelayGroup, FloatingPortal } from '@floating-ui/react';
